import Text from '@bufferapp/ui/Text';
import Tooltip from '@bufferapp/ui/Tooltip';
import * as Dialog from '@radix-ui/react-dialog';
import isEqual from 'lodash/isEqual';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AIAssistant } from '~/ai/components/AIAssistant';
import { selectContentGenerationStatus } from '~/ai/state/contentGeneration/slice';
import { useUploader } from '~/uploads/hooks/useUploader';
import { getPlainText } from '../../../editor/BufferEditor';
import { BufferEditor } from '../../../editor/BufferEditor/types.plate';
import { IdeasEditor } from '../../../editor/IdeasEditor';
import { IntegrationsBar } from '../../../integrations-bar/IntegrationsBar';
import { IntegrationsContext } from '../../../integrations-bar/types';
import { useIntegrationServices } from '../../../integrations-bar/useIntegrationServices';
import { useAppSelector } from '../../../store';
import {
  MAX_THUMBNAILS,
  MediaManager,
} from '../../../uploads/components/MediaManager';
import {
  selectCompletedUploads,
  selectIncludedMedia,
  selectPendingCount,
} from '../../../uploads/state/selectors';
import { Idea, IdeaContent, NewIdea } from '../../types';
import { isMediaConvertibleToPost } from '../../utils';
import { IDEAS_UPLOADER_ID } from '../config';
import { useCMDEnterKeys } from '../hooks/useCMDEnterKeys';
import { useVariableHeight } from '../hooks/useVariableHeight';
import { ConfirmCloseModal } from '../modals/ConfirmCloseModal';

import {
  ButtonWrapper,
  CloseButton,
  IdeatorContent,
  IdeatorInnerWrapper,
  IdeatorWrapper,
  IntegrationsWrapper,
  MediaManagerWrapper,
  StyledButton,
  StyledNotice,
  StyledOverlay,
} from './styles';

type EditorProps = {
  currentIdea: NewIdea | undefined;
  onSaveIdea: () => void;
  showErrorMessage: boolean;
  containerRef: HTMLElement | null | undefined;
  onCreatePost: () => void;
  onOpenChange: (open: boolean) => void;
  plateEditor: BufferEditor;
};

const Editor = ({
  currentIdea,
  onSaveIdea,
  onCreatePost,
  showErrorMessage,
  containerRef,
  onOpenChange,
  plateEditor,
}: EditorProps): JSX.Element => {
  const wrapperRef = React.useRef(null);

  const [editorValue, setEditorValue] = useState(null);
  const [currentText, setCurrentText] = useState<string | null>(null);
  const [ctaPostTooltipLabel, setCtaPostTooltipLabel] = useState<string | null>(
    null
  );

  const { onFileReady } = useUploader({ id: IDEAS_UPLOADER_ID });

  const pendingCount = useAppSelector((state) =>
    selectPendingCount(state, IDEAS_UPLOADER_ID)
  );
  const completedUploads = useAppSelector((state) =>
    selectCompletedUploads(state, IDEAS_UPLOADER_ID)
  );
  const completedCount = completedUploads.length;

  const isUploading = pendingCount > 0;
  const context: IntegrationsContext = 'ideasEditor';

  const { services, activeService } = useIntegrationServices({
    onFileReady,
    context,
  });

  const media = useAppSelector((state) =>
    selectIncludedMedia(state, IDEAS_UPLOADER_ID)
  );

  const { contentGenerationInProgress, contentGenerationError } =
    useAppSelector((state) => selectContentGenerationStatus(state));

  const { t } = useTranslation();

  useEffect(() => {
    if (editorValue) setCurrentText(getPlainText(plateEditor).trim());
  }, [editorValue, plateEditor]);

  const actionButtonsDisabled = useMemo(() => {
    const hasMedia = completedCount !== 0;
    return isUploading || (!currentText && !hasMedia);
  }, [currentText, isUploading, completedCount]);

  const postButtonsDisabled = useMemo(() => {
    const content: IdeaContent = { text: currentText || undefined, media };

    const idea: NewIdea = { content };
    const { valid, message } = isMediaConvertibleToPost(idea as Idea);
    setCtaPostTooltipLabel(message);
    return !valid;
  }, [currentText, media]);

  const { editorHeightStyles } = useVariableHeight({ completedCount });

  const tooltipLabel =
    actionButtonsDisabled && !contentGenerationInProgress
      ? t('content.editor.disabledTooltip')
      : '';

  const [isOpen, setIsOpen] = useState(false);

  const onOpenChangeConfirmModal = (open: boolean) => {
    // Defaults to [] when currentIdea is a new idea
    const currentIdeaMedia = currentIdea?.content?.media || [];
    const isIdeaTextUpdated =
      currentText !== (currentIdea?.content?.text || null);
    const isIdeaMediaUpdated = !isEqual(
      media.map((el) => el.url),
      currentIdeaMedia.map((el) => el.url)
    );
    const isIdeaUpdated = isIdeaTextUpdated || isIdeaMediaUpdated;
    if (open) {
      if (isIdeaUpdated) {
        setIsOpen(true);
      } else {
        setIsOpen(false);
        onOpenChange(false);
      }
    } else {
      setIsOpen(false);
    }
  };

  const preventDefault = useCallback(
    (e: { preventDefault: () => void }) => e.preventDefault(),
    []
  );

  const onLeave = (event: React.MouseEvent | KeyboardEvent) => {
    preventDefault(event);
    onOpenChangeConfirmModal(true);
  };

  const onOverlayClick = (event: React.MouseEvent | KeyboardEvent) => {
    const target = event.target as HTMLElement;
    const wrapper = wrapperRef.current as HTMLElement | null;

    if (wrapper?.contains(target)) return;

    onLeave(event);
  };

  useCMDEnterKeys(
    onSaveIdea,
    !actionButtonsDisabled && !contentGenerationInProgress
  );

  return (
    <>
      <StyledOverlay onClick={onOverlayClick} />
      <IdeatorWrapper
        id="ideas-content-wrapper"
        onInteractOutside={preventDefault}
        onEscapeKeyDown={onLeave}
        ref={wrapperRef}
      >
        {(showErrorMessage || contentGenerationError) && (
          <StyledNotice type="alert" disableAnimation="true">
            <Text>{contentGenerationError || t('content.editor.error')}</Text>
          </StyledNotice>
        )}
        <IdeatorInnerWrapper>
          <IdeatorContent style={editorHeightStyles}>
            <IdeasEditor editor={plateEditor} onChange={setEditorValue} />
          </IdeatorContent>
          <MediaManagerWrapper id="ideas-media-manager">
            <MediaManager id="ideas" containerRef={containerRef} />
          </MediaManagerWrapper>
          <IntegrationsWrapper>
            <IntegrationsBar
              context={context}
              services={services}
              activeService={activeService}
              disableBar={completedCount + pendingCount >= MAX_THUMBNAILS}
            />
            <AIAssistant editor={plateEditor} />
          </IntegrationsWrapper>
          <ButtonWrapper id="ideas-actions">
            <Tooltip label={tooltipLabel || ctaPostTooltipLabel}>
              <StyledButton
                type="secondary"
                label={t('content.common.createPost')}
                onClick={onCreatePost}
                disabled={
                  actionButtonsDisabled ||
                  postButtonsDisabled ||
                  contentGenerationInProgress
                }
              />
            </Tooltip>
            <Tooltip label={tooltipLabel}>
              <StyledButton
                type="primary"
                label={t('content.common.saveIdea')}
                onClick={onSaveIdea}
                disabled={actionButtonsDisabled || contentGenerationInProgress}
              />
            </Tooltip>
          </ButtonWrapper>
          <Dialog.Close asChild>
            <CloseButton size="large" verticalAlign="top" onClick={onLeave} />
          </Dialog.Close>
        </IdeatorInnerWrapper>

        <Dialog.Root open={isOpen} onOpenChange={onOpenChangeConfirmModal}>
          <Dialog.Portal container={containerRef}>
            <ConfirmCloseModal
              onCancel={() => onOpenChangeConfirmModal(false)}
              onConfirmClose={() => onOpenChange(false)}
            />
          </Dialog.Portal>
        </Dialog.Root>
      </IdeatorWrapper>
    </>
  );
};

export default Editor;
